// Microbe RTS — the headless, deterministic simulation.
//
// createSim() returns the full Sim interface (types.ts). The sim OWNS all game state and is the
// only thing that mutates it; render + ui read sim.state and push intent via sim.command(...).
//
// Tick order (per the bible): bot -> orders/movement -> combat -> economy -> capture ->
// production -> clocks. Deterministic: all randomness comes from a mulberry32 stream seeded off
// state.seed. No DOM / window / canvas — runs under node.

import type {
  Sim, Command, Vec, Side, Unit, UnitKind, WorldNode, RateSummary,
} from '../types.js';
import { MUTATIONS, SPECIATION_GATES, DEFAULT_DIFFICULTY, WORLD, CAPTURE, WILD, BOT, BASE_DEFENSE } from '../config.js';
import type { Mutation, Difficulty, BotProfile } from '../config.js';
import {
  buildInitialState, mulberry32, nodeById, unitById, dist, ownedCapturable, ownsNodeType,
  updateCapture, pushEvent, unitSpec, spawnGrazerAt, siegeReach,
} from './world.js';
import type { SimCtx } from './world.js';
import {
  moveUnits, separation, deriveColonies, processProduction, queueUnit, queueBrood,
  newProdQueue, canAfford, pay, classSpeedMult, resolveHealing, resolveRegen, applyHpMutationRetro,
  buildTimeMultOf,
} from './units.js';
import type { ProdQueue } from './units.js';
import { resolveCombat } from './combat.js';
import { resolveBaseDefense } from './defense.js';
import { resolveTurrets, placeTurretAt, repairTurrets, turretsUnlocked, turretCap } from './turrets.js';
import { applyEconomy, computeRates } from './economy.js';
import { tickClocks } from './clocks.js';
import { tickBot, createBotMemory } from './bot.js';
import type { BotDeps } from './bot.js';

export interface CreateSimOptions {
  seed?: number;
  difficulty?: Difficulty;
  /** Sandbox / god-view: also drive the 'you' side with the macro-AI (bot vs bot). Default false. */
  bothBots?: boolean;
  /** Difficulty for the 'you' bot when bothBots is set. Defaults to `difficulty`. */
  difficultyYou?: Difficulty;
  /** Plug-and-play: hand the 'bot' side a full custom profile (overrides `difficulty`). Same object
   *  the sandbox authors, so a profile tuned in the god-view harness drops straight into the game. */
  profile?: BotProfile;
  /** Custom profile for the 'you' side in bothBots mode (overrides `difficultyYou`). */
  profileYou?: BotProfile;
  /** Human-vs-human PvP: run NEITHER bot AI (both colonies are player-driven via command(cmd, side)).
   *  Takes precedence over bothBots — pvp => no bots at all. Default false (solo: bot drives 'bot'). */
  pvp?: boolean;
}

// Formation row depth (world units along the travel axis) by kind: positive = FRONT line (toward the
// destination), negative = BACK. Tanks lead, ranged trail far enough to fire over the line.
const FORMATION_DEPTH: Record<string, number> = {
  // Front→back per Ara's spec: tanks (engulfer/phalanx) lead, then foot soldiers (lancer/toxifer),
  // then ranged (stinger), and healers at the very back so they never run into the line.
  phalanx: 60, // tank — front line
  engulfer: 48, // tank — front line
  lancer: 0, // foot soldier — center
  toxifer: 0, // foot soldier — center (short-range poison, fights in the line)
  drone: 0, // fast skirmisher — center
  worker: 0,
  stinger: -80, // ranged — trails behind the foot line to fire over it
  healer: -150, // fragile support — kept far behind the line
};
const FORMATION_SPACING = 40; // lateral gap within a row (>= unit radius sums, so separation() stays calm)
const REAR_GUARD_DIST = 170; // how far behind the fighting group's centroid healers hold on an attack

// Position selected healers BEHIND the fighting group, on the far side from the threat, so they
// support the line without ever charging into it (Ara: healers "hang right behind" the group and
// disregard the exact attack tap). No-op when there are no healers or no combat units to anchor to.
function placeHealersBehind(
  healers: Unit[],
  groupUnits: Unit[],
  threat: Vec,
  speedCap: number | null,
): void {
  if (healers.length === 0 || groupUnits.length === 0) return;
  let cx = 0;
  let cy = 0;
  for (const u of groupUnits) { cx += u.pos.x; cy += u.pos.y; }
  cx /= groupUnits.length;
  cy /= groupUnits.length;
  // Unit vector pointing AWAY from the threat (the group's rear).
  let bx = cx - threat.x;
  let by = cy - threat.y;
  const bl = Math.hypot(bx, by) || 1;
  bx /= bl;
  by /= bl;
  const px = -by; // lateral
  const py = bx;
  const rx = cx + bx * REAR_GUARD_DIST;
  const ry = cy + by * REAR_GUARD_DIST;
  healers.forEach((h, i) => {
    const lateral = (i - (healers.length - 1) / 2) * FORMATION_SPACING;
    h.moveTarget = {
      x: Math.max(8, Math.min(WORLD.width - 8, rx + px * lateral)),
      y: Math.max(8, Math.min(WORLD.height - 8, ry + py * lateral)),
    };
    h.moveSpeedCap = speedCap;
    h.focusId = null;
    h.attackNodeId = null;
  });
}

export function createSim(opts?: CreateSimOptions): Sim {
  const difficulty: Difficulty = opts?.difficulty ?? DEFAULT_DIFFICULTY;
  const difficultyYou: Difficulty = opts?.difficultyYou ?? difficulty;
  const bothBots = opts?.bothBots ?? false;
  // PvP: both colonies are human-driven — disable ALL bot AI (takes precedence over bothBots).
  const pvp = opts?.pvp ?? false;
  const seed = ((opts?.seed ?? 1) >>> 0) || 1;

  // Resolve each side's profile: explicit custom profile wins, else the difficulty preset. The 'you'
  // side only gets a profile in bothBots mode (otherwise it's human-driven → null, no scaling).
  //
  // FAIRNESS INVARIANT (enforced here, structurally): NO bot ever gets a handicap of any kind — pure
  // skill decides every match. We neutralize the three handicap dials on every resolved profile, so no
  // profile (shipped or self-play-evolved) can hand a side extra income, bigger waves, or map vision.
  // A bot's difficulty comes ONLY from its strategy (the genome). incomeMult → economy.ts; waveSizeMult
  // → bot.ts launchWave; fogOmniscient is already inert (unread). Fresh copies, so BOT_PROFILES is intact.
  const fair = (p: BotProfile): BotProfile => ({ ...p, incomeMult: 1, waveSizeMult: 1, fogOmniscient: false });
  const profBot: BotProfile = fair(opts?.profile ?? BOT[difficulty]);
  const profYou: BotProfile | null = bothBots ? fair(opts?.profileYou ?? BOT[difficultyYou]) : null;

  const state = buildInitialState(seed, difficulty);
  const ctx: SimCtx = {
    rng: mulberry32(seed ^ 0x9e3779b9),
    difficulty,
    profiles: { bot: profBot, you: profYou },
  };
  const q: ProdQueue = newProdQueue();
  const memBot = createBotMemory(profBot);
  // Sandbox two-bot mode: a second AI drives the 'you' side with its own memory + threat clock.
  const memYou = profYou ? createBotMemory(profYou) : null;
  // Keep each fixed lair populated: if its grazer is farmed to death, respawn one AT THE SAME lair
  // after WILD.respawnSeconds — grazers stay bound to their spot for the whole match.
  function tickWild(dt: number): void {
    for (const lair of state.lairs) {
      let occupied = false;
      for (const u of state.units) {
        // A grazer belongs to this lair iff its (immutable) guard point is the lair centre. A
        // grazer that's off chasing still counts — it lives and will leash home.
        if (
          u.side === 'wild' && u.hp > 0 && u.guardPos &&
          u.guardPos.x === lair.pos.x && u.guardPos.y === lair.pos.y
        ) {
          occupied = true;
          break;
        }
      }
      if (occupied) {
        lair.respawn = WILD.respawnSeconds;
        continue;
      }
      lair.respawn -= dt;
      if (lair.respawn <= 0) {
        spawnGrazerAt(state, lair.pos);
        lair.respawn = WILD.respawnSeconds;
      }
    }
  }

  // ---- Evolution (gates + affordability, data-driven from config.MUTATIONS) ----
  function mutationById(id: string): Mutation | undefined {
    return MUTATIONS.find((m) => m.id === id);
  }
  function passesGates(side: Side, mut: Mutation): boolean {
    const col = state.colonies[side];
    const lvl = col.genome[mut.id] ?? 0;
    if (lvl >= mut.maxLevel) return false;
    if (mut.gateTier && col.tier < mut.gateTier) return false;
    if (mut.requires) {
      for (const r of mut.requires) if ((col.genome[r] ?? 0) <= 0) return false;
    }
    if (mut.gatePop && col.popUsed < mut.gatePop) return false;
    if (mut.gateNodeType && !ownsNodeType(state, side, mut.gateNodeType)) return false;
    if (mut.gateNodes && ownedCapturable(state, side).length < mut.gateNodes) return false;
    if (mut.keystone) {
      for (const kid of col.keystones) {
        const km = mutationById(kid);
        if (km && km.tier === mut.tier) return false; // one keystone per tier band
      }
    }
    const specTier = mut.effect?.speciateToTier;
    if (specTier) {
      const g = SPECIATION_GATES[specTier as 2 | 3 | 4];
      if (g) {
        if (col.popUsed < g.pop) return false;
        if (ownedCapturable(state, side).length < g.nodes) return false;
      }
    }
    return true;
  }
  function affordable(side: Side, mut: Mutation): boolean {
    return canAfford(state.colonies[side].res, mut.cost);
  }
  function canEvolveFor(side: Side, id: string): boolean {
    const mut = mutationById(id);
    if (!mut) return false;
    return passesGates(side, mut) && affordable(side, mut);
  }
  function offeredFor(side: Side): string[] {
    const out: string[] = [];
    for (const mut of MUTATIONS) {
      if (passesGates(side, mut) && affordable(side, mut)) out.push(mut.id);
    }
    return out;
  }
  function buyMutation(side: Side, id: string): boolean {
    const mut = mutationById(id);
    if (!mut) return false;
    if (!passesGates(side, mut) || !affordable(side, mut)) return false;
    const col = state.colonies[side];
    pay(col.res, mut.cost);
    col.genome[id] = (col.genome[id] ?? 0) + 1;
    if (mut.keystone && !col.keystones.includes(id)) col.keystones.push(id);
    // HP boosts apply RETROACTIVELY to units already on the field (this one level's factor).
    if (mut.effect?.hpMult) applyHpMutationRetro(state, side, mut.effect.hpMult);
    const specTier = mut.effect?.speciateToTier;
    if (specTier) col.tier = Math.max(col.tier, specTier);
    pushEvent(state, { kind: 'evolved', side, text: mut.name, at: state.clocks.sim });
    return true;
  }

  const botDeps: BotDeps = { q, mem: memBot, evolve: buyMutation, offered: offeredFor };
  // Same side-aware prod queue + evolve/offered surface; only the per-bot memory differs.
  const botDepsYou: BotDeps | null = memYou
    ? { q, mem: memYou, evolve: buyMutation, offered: offeredFor }
    : null;

  // ---- The tick ----
  function update(dt: number): void {
    const c = state.clocks;
    if (c.paused || state.result.over || dt <= 0) return;
    deriveColonies(state);
    tickWild(dt);
    // PvP (both sides human): run NO bot AI. Solo: bot drives 'bot'. bothBots (sandbox): also drives
    // 'you'. pvp takes precedence — no bots at all.
    if (!pvp) {
      tickBot(state, ctx, 'bot', 'you', profBot, botDeps, dt);
      if (botDepsYou && profYou) tickBot(state, ctx, 'you', 'bot', profYou, botDepsYou, dt);
    }
    moveUnits(state, dt);
    separation(state, dt);
    resolveCombat(state, dt);
    resolveHealing(state, dt);
    resolveRegen(state, dt);
    resolveBaseDefense(state, dt);
    resolveTurrets(state, dt);
    applyEconomy(state, ctx, dt);
    updateCapture(state, dt);
    processProduction(state, q, dt);
    tickClocks(state, dt);
    // Threat clock now lives per-bot in BotMemory; decrement at the clocks phase (after the bot's
    // check earlier this tick) to preserve the original check-then-decrement ordering. Mirror the
    // primary 'bot' back onto state.clocks so legacy readers (test/smoke.ts) stay valid.
    memBot.threatTimer -= dt;
    if (memYou) memYou.threatTimer -= dt;
    state.clocks.botThreatTimer = memBot.threatTimer;
    state.clocks.botWave = memBot.wave;
  }

  // ---- Commands ----
  // Side-scoped live selection: the given side's selected, alive, own units. Solo passes 'you'
  // (default), so behavior is unchanged; PvP passes each input frame's origin side.
  function sideSelection(side: Side): Unit[] {
    const out: Unit[] = [];
    for (const id of state.selectionBySide[side]) {
      const u = unitById(state, id);
      if (u && u.side === side && u.hp > 0) out.push(u);
    }
    return out;
  }

  // Cohesion speed cap for a party: the SLOWEST member's live speed (base × class mutation), so a
  // mixed-kind group — moving OR attacking — advances together instead of the fast units racing
  // ahead of the tanks. Returns null for an empty party (no cap). Side-scoped so the class-speed
  // mutation reads the commanding colony's genome.
  function partySpeedCap(units: Unit[], side: Side): number | null {
    let cap = Infinity;
    for (const u of units) {
      const s = unitSpec(u.kind).speed * classSpeedMult(state.colonies[side], u.kind);
      if (s < cap) cap = s;
    }
    return cap === Infinity ? null : cap;
  }

  function command(cmd: Command, side: Side = 'you'): void {
    switch (cmd.t) {
      case 'select': {
        const ids: string[] = [];
        for (const id of cmd.ids) {
          const u = unitById(state, id);
          if (u && u.side === side && u.hp > 0) ids.push(id);
        }
        state.selectionBySide[side] = ids;
        break;
      }
      case 'clearSelection':
        state.selectionBySide[side] = [];
        break;
      case 'move': {
        const sel = sideSelection(side);
        // The party moves as one: cap everyone to the SLOWEST selected member's live speed so mixed-kind
        // groups stay together instead of the fast units racing ahead. Single-kind selection caps to its
        // own speed (a no-op).
        const speedCap = partySpeedCap(sel, side);

        // Formation: offset each unit's destination along the travel axis by ROLE so tanks form the
        // front line (toward the target) and ranged units trail at the very back, with a lateral fan
        // within each row. Only for multi-unit moves that actually travel somewhere.
        let cx = 0;
        let cy = 0;
        for (const u of sel) {
          cx += u.pos.x;
          cy += u.pos.y;
        }
        cx /= sel.length || 1;
        cy /= sel.length || 1;
        const dx = cmd.pos.x - cx;
        const dy = cmd.pos.y - cy;
        const dlen = Math.hypot(dx, dy);
        const useFormation = sel.length > 1 && dlen > 1e-3;
        const fx = useFormation ? dx / dlen : 0; // forward (toward destination)
        const fy = useFormation ? dy / dlen : 0;
        const px = -fy; // lateral (perpendicular)
        const py = fx;
        const rowTotal = new Map<number, number>();
        const rowSeen = new Map<number, number>();
        if (useFormation) for (const u of sel) {
          const d = FORMATION_DEPTH[u.kind] ?? 0;
          rowTotal.set(d, (rowTotal.get(d) ?? 0) + 1);
        }

        for (const u of sel) {
          let tx = cmd.pos.x;
          let ty = cmd.pos.y;
          if (useFormation) {
            const depth = FORMATION_DEPTH[u.kind] ?? 0;
            const n = rowTotal.get(depth) ?? 1;
            const idx = rowSeen.get(depth) ?? 0;
            rowSeen.set(depth, idx + 1);
            const lateral = (idx - (n - 1) / 2) * FORMATION_SPACING;
            // Keep the offset target reachable: inside the world, inset a hair so the unit doesn't
            // pin against the position clamp (which would leave moveTarget/moveSpeedCap stuck).
            tx = Math.max(8, Math.min(WORLD.width - 8, cmd.pos.x + fx * depth + px * lateral));
            ty = Math.max(8, Math.min(WORLD.height - 8, cmd.pos.y + fy * depth + py * lateral));
          }
          u.moveTarget = { x: tx, y: ty };
          u.moveSpeedCap = speedCap;
          u.focusId = null;
          u.attackNodeId = null; // a fresh move ends any siege
        }
        break;
      }
      case 'focus': {
        const enemy = unitById(state, cmd.enemyId);
        if (!enemy || enemy.side === side || enemy.hp <= 0) break;
        // Keep rank while charging: cap the whole attacking party to its slowest member so the tanks
        // arrive with the fast units instead of the squad breaking apart on the way in.
        const sel = sideSelection(side);
        const war = sel.filter((u) => u.kind !== 'healer');
        const healers = sel.filter((u) => u.kind === 'healer');
        const speedCap = partySpeedCap(war, side);
        if (war.length === 0) {
          // Healers-only selection can't fight — treat the tap as a plain move so it isn't a dead no-op.
          const cap = partySpeedCap(healers, side);
          for (const h of healers) {
            h.moveTarget = { x: enemy.pos.x, y: enemy.pos.y };
            h.moveSpeedCap = cap;
            h.focusId = null;
            h.attackNodeId = null;
          }
          break;
        }
        for (const u of war) {
          u.focusId = cmd.enemyId;
          u.moveTarget = null;
          u.moveSpeedCap = speedCap; // cohesion cap holds through the chase (gated on focusId in moveUnits)
          u.attackNodeId = null;
        }
        // Healers trail behind the fighters instead of charging the target.
        placeHealersBehind(healers, war, enemy.pos, speedCap);
        break;
      }
      case 'claim': {
        // SEED: send the selection onto a crater. A drone that reaches the core channels the seed
        // (updateCapture); the first Home seed is free + crowns her Queen, later seeds cost SEED_COST.
        const node = nodeById(state, cmd.nodeId);
        if (!node) break;
        for (const u of sideSelection(side)) {
          u.moveTarget = { x: node.pos.x, y: node.pos.y };
          u.moveSpeedCap = null;
          u.focusId = null;
          u.attackNodeId = null;
        }
        break;
      }
      case 'attack': {
        // Siege an enemy node: node damage is by WEAPON DAMAGE + RANGE (coreWarriorDps counts any unit
        // whose weapon reaches the core). So MELEE packs onto the core in a golden-angle sunflower, but
        // RANGED units (stinger/toxifer) hold at their weapon range — shelling the core from outside the
        // poison ooze instead of suiciding into it. `attackNodeId` re-homes them if separation drifts.
        const node = nodeById(state, cmd.nodeId);
        if (!node) break;
        const sel = sideSelection(side);
        const war = sel.filter((u) => u.kind !== 'healer');
        const healers = sel.filter((u) => u.kind === 'healer');
        if (war.length === 0) {
          // Healers-only selection can't siege — plain move toward the node so the tap does something.
          const cap = partySpeedCap(healers, side);
          for (const h of healers) {
            h.moveTarget = { x: node.pos.x, y: node.pos.y };
            h.moveSpeedCap = cap;
            h.focusId = null;
            h.attackNodeId = null;
          }
          break;
        }
        const core = node.radius * CAPTURE.coreRadiusFrac;
        const packR = core * 0.6;
        const melee = war.filter((u) => unitSpec(u.kind).range <= 0);
        const ranged = war.filter((u) => unitSpec(u.kind).range > 0);
        const nMelee = melee.length;
        const nRanged = ranged.length;
        // Cohesion cap so the siege line advances together — tanks reach the core alongside the fast
        // units instead of the pack strung out across the approach.
        const speedCap = partySpeedCap(war, side);
        // The side the army is coming from — ranged units line up in a facing ARC on this side and
        // shell the core, rather than encircling the whole node (Ara).
        let ax = 0;
        let ay = 0;
        for (const u of war) { ax += u.pos.x; ay += u.pos.y; }
        ax = war.length ? ax / war.length : node.pos.x;
        ay = war.length ? ay / war.length : node.pos.y + 1;
        const approach = Math.atan2(ay - node.pos.y, ax - node.pos.x);
        const ARC = 1.9; // total spread of the ranged semicircle (radians; ~109°)
        // Melee: packed on the core in a golden-angle sunflower (unchanged).
        melee.forEach((u, k) => {
          const ang = k * 2.399963; // golden angle -> deterministic even fill
          const rr = nMelee <= 1 ? 0 : packR * Math.sqrt((k + 0.5) / nMelee);
          u.moveTarget = { x: node.pos.x + Math.cos(ang) * rr, y: node.pos.y + Math.sin(ang) * rr };
          u.moveSpeedCap = speedCap;
          u.focusId = null;
          u.attackNodeId = node.id;
        });
        // Ranged: a facing arc centered on the approach angle, at ~80% of weapon reach.
        ranged.forEach((u, k) => {
          const rr = siegeReach(u, node) * 0.8;
          const off = nRanged <= 1 ? 0 : (k / (nRanged - 1) - 0.5) * ARC;
          const ang = approach + off;
          u.moveTarget = { x: node.pos.x + Math.cos(ang) * rr, y: node.pos.y + Math.sin(ang) * rr };
          u.moveSpeedCap = speedCap;
          u.focusId = null;
          u.attackNodeId = node.id;
        });
        // Healers hold well behind the siege line.
        placeHealersBehind(healers, war, node.pos, speedCap);
        break;
      }
      case 'assign': {
        // Workers are gone (nodes auto-work) — 'assign' now just walks the selection onto the node.
        const node = nodeById(state, cmd.nodeId);
        if (!node) break;
        for (const u of sideSelection(side)) {
          u.moveTarget = { x: node.pos.x, y: node.pos.y };
          u.moveSpeedCap = null;
          u.focusId = null;
          u.attackNodeId = null;
        }
        break;
      }
      case 'garrison': {
        // Pull the selection back to one of YOUR nodes to turtle: hold at the node (POI armor +
        // base defense cover them) instead of chasing out on aggro.
        const node = nodeById(state, cmd.nodeId);
        if (!node || node.owner !== side) break;
        for (const u of sideSelection(side)) {
          u.moveTarget = { x: node.pos.x, y: node.pos.y };
          u.moveSpeedCap = null;
          u.focusId = null;
          u.attackNodeId = null;
          u.bandStance = 'hold';
        }
        break;
      }
      case 'restoreOrders': {
        // Put back the movement orders captured before a stray tap (used by double-tap-to-clear so
        // a band en route to a real destination isn't redirected by the throwaway first tap).
        for (const o of cmd.orders) {
          const u = unitById(state, o.id);
          if (!u || u.side !== side || u.hp <= 0) continue;
          u.moveTarget = o.moveTarget ? { x: o.moveTarget.x, y: o.moveTarget.y } : null;
          u.moveSpeedCap = o.moveSpeedCap;
          u.focusId = o.focusId;
          u.attackNodeId = o.attackNodeId;
          u.assignedNode = o.assignedNode;
          u.bandStance = o.bandStance; // garrison forced 'hold'; undo restores the prior stance
        }
        break;
      }
      case 'rally': {
        const node = nodeById(state, cmd.nodeId);
        if (!node || node.owner !== side) break;
        node.rally = { x: cmd.pos.x, y: cmd.pos.y };
        break;
      }
      case 'produce': {
        if (queueUnit(state, q, side, cmd.nodeId, cmd.kind)) {
          const node = nodeById(state, cmd.nodeId);
          pushEvent(state, {
            kind: 'produced',
            side,
            pos: node ? { ...node.pos } : undefined,
            text: cmd.kind,
            at: state.clocks.sim,
          });
        }
        break;
      }
      case 'buildBrood':
        queueBrood(state, q, side, cmd.nodeId);
        break;
      case 'stance': {
        for (const u of sideSelection(side)) {
          if (u.kind === 'healer') continue;
          u.bandStance = cmd.stance;
        }
        break;
      }
      case 'evolve':
        buyMutation(side, cmd.mutationId);
        break;
      case 'assignGroup': {
        const slot = cmd.slot | 0;
        if (slot < 0 || slot >= state.groupsBySide[side].length) break;
        state.groupsBySide[side][slot] = [...state.selectionBySide[side]];
        break;
      }
      case 'placeTurret':
        placeTurretAt(state, side, cmd.pos); // validates unlock + cap + ring-clamp + >=60deg + cost
        break;
      case 'repairTurrets':
        repairTurrets(state, side);
        break;
      case 'pause':
        state.clocks.paused = cmd.on;
        break;
    }
  }

  // ---- Read helpers ----
  function pickUnitAt(world: Vec, radiusWorld: number, side?: Side): Unit | null {
    let best: Unit | null = null;
    let bestD = Infinity;
    for (const u of state.units) {
      if (u.hp <= 0) continue;
      if (side && u.side !== side) continue;
      const d = dist(world, u.pos);
      if (d <= radiusWorld + u.radius && d < bestD) {
        bestD = d;
        best = u;
      }
    }
    return best;
  }
  function pickNodeAt(world: Vec, radiusWorld: number, expandToDefense = false): WorldNode | null {
    let best: WorldNode | null = null;
    let bestD = Infinity;
    for (const n of state.nodes) {
      // For SELECTION (nothing in hand) the whole defended footprint is tappable — out to the node's
      // ranged-fire ring (the outer dashed ring you see), so a friendly parked inside can't shrink the
      // node's hitbox. Command-mode picks stay tight (default), so attack/claim don't fire from afar.
      let reach = n.radius;
      if (expandToDefense && (n.owner === 'you' || n.owner === 'bot')) {
        reach = Math.max(reach, n.type === 'home' ? BASE_DEFENSE.homeRange : BASE_DEFENSE.colonyRange);
      }
      const d = dist(world, n.pos);
      if (d <= radiusWorld + reach && d < bestD) {
        bestD = d;
        best = n;
      }
    }
    return best;
  }
  function rates(side: Side): RateSummary {
    return computeRates(state, ctx, side);
  }
  // Staggered queue view for the HUD: only the head job (lowest buildIndex) at this node is building,
  // so it reports a live 0..1 fraction; the rest are pending at 0. Reads the private ProdQueue.
  function productionAt(nodeId: string): { kind: UnitKind; fraction: number }[] {
    const jobs = q.units
      .filter((j) => j.nodeId === nodeId)
      .sort((a, b) => a.buildIndex - b.buildIndex);
    return jobs.map((j, i) => ({
      kind: j.kind,
      // Denominator must match how timeLeft was seeded in queueUnit (buildSeconds * buildTimeMult),
      // or the bar starts mid-fill once Rapid Mitosis is teched.
      fraction: i === 0 ? 1 - j.timeLeft / (unitSpec(j.kind).buildSeconds * buildTimeMultOf(state.colonies[j.side])) : 0,
    }));
  }

  const sim: Sim = {
    state,
    update,
    command,
    pickUnitAt,
    pickNodeAt,
    rates,
    canEvolve: (id: string) => canEvolveFor('you', id),
    offeredMutations: (side: Side) => offeredFor(side),
    nodeById: (id: string) => nodeById(state, id),
    unitById: (id: string) => unitById(state, id),
    turretsUnlocked: (side: Side) => turretsUnlocked(state.colonies[side]),
    turretCap: (side: Side) => turretCap(state.colonies[side]),
    production: (nodeId: string) => productionAt(nodeId),
  };
  return sim;
}

// Convenience re-export for consumers that want the low-level builder.
export { buildInitialState } from './world.js';
